import { FileCategory } from '../models';

/** Accept attribute value for <input type="file"> per category. */
export function acceptForCategory(cat: FileCategory): string {
	switch (cat) {
		case 'video':
		case 'mask':
			return 'video/*';
		case 'images':
			return 'image/*';
		case 'audios':
			// MP3 / AAC / WAV / OGG. MIMEs cover most OSes; extensions are a
			// fallback for pickers that filter by extension only.
			return 'audio/mpeg,audio/mp3,audio/aac,audio/wav,audio/wave,audio/x-wav,audio/ogg,.mp3,.aac,.wav,.ogg';
		case 'fonts':
			return '.ttf,.otf,.woff,.woff2';
		case 'subtitles':
			// SRT / WebVTT. Browsers assign inconsistent MIME types (text/vtt,
			// application/x-subrip, text/plain, or none), so extensions are the
			// reliable filter — mirrors the fonts case.
			return '.srt,.vtt,text/vtt,application/x-subrip';
		default:
			return '*/*';
	}
}

const FONT_EXTENSIONS = ['.ttf', '.otf', '.woff', '.woff2'];
const SUBTITLE_EXTENSIONS = ['.srt', '.vtt'];
const AUDIO_MIMES = [
	'audio/mpeg', 'audio/mp3',
	'audio/aac',
	'audio/wav', 'audio/wave', 'audio/x-wav',
	'audio/ogg',
];
const AUDIO_EXTENSIONS = ['.mp3', '.aac', '.wav', '.ogg'];

/**
 * Validate a file against a category. Returns an error message when rejected,
 * `null` when the file is acceptable.
 *
 * Video/mask accept `video/*` MIME types. Images accept `image/*`. Audios accept
 * `audio/*`. Fonts are checked by extension since browsers assign a variety of
 * MIME types (and often `application/octet-stream`).
 */
export function validateFileForCategory(file: File, category: FileCategory): string | null {
	const mime = file.type ?? '';
	const name = file.name.toLowerCase();

	switch (category) {
		case 'video':
		case 'mask':
		case 'video-mask':
			if (!mime.startsWith('video/')) {
				return `Invalid file type: expected video, got ${mime || 'unknown'}`;
			}
			return null;
		case 'images':
			if (!mime.startsWith('image/')) {
				return `Invalid file type: expected image, got ${mime || 'unknown'}`;
			}
			return null;
		case 'audios':
			// MP3 / AAC / WAV / OGG. Accept by MIME (authoritative) or by
			// extension (fallback — some browsers fail to set the audio MIME
			// on drag-drop).
			if (!AUDIO_MIMES.includes(mime) && !AUDIO_EXTENSIONS.some(ext => name.endsWith(ext))) {
				return `Invalid file type: expected audio (${AUDIO_EXTENSIONS.join('/')}), got ${mime || name}`;
			}
			return null;
		case 'fonts':
			if (!FONT_EXTENSIONS.some(ext => name.endsWith(ext))) {
				return `Invalid file type: expected font (${FONT_EXTENSIONS.join('/')}), got ${name}`;
			}
			return null;
		case 'subtitles':
			// SRT / WebVTT — checked by extension since browsers assign a variety
			// of MIME types (text/vtt, application/x-subrip, text/plain, or none).
			if (!SUBTITLE_EXTENSIONS.some(ext => name.endsWith(ext))) {
				return `Invalid file type: expected subtitles (${SUBTITLE_EXTENSIONS.join('/')}), got ${name}`;
			}
			return null;
		default:
			return `Unsupported category: ${category}`;
	}
}
